import { supabase } from '../../lib/supabase';
import type { Meeting } from '../../types';

export interface MeetingFilters {
  community?: string;
  year?: number;
  quarter?: number;
  month?: number;
}

export interface MeetingStats {
  totalMeetings: number;
  meetingsPerMonth: {
    year: number;
    month: number;
    count: number;
  }[];
  communitiesWithMeetings: string[];
}

export const supabaseMeetingService = {
  getAll: async (filters?: MeetingFilters): Promise<Meeting[]> => {
    try {
      console.log('Fetching meetings with filters:', filters);
      
      let query = supabase
        .from('comm_meetings')
        .select('meet_comm, meet_date, meet_minutes');

      // Apply filters
      if (filters?.community) {
        query = query.eq('meet_comm', filters.community);
      }

      if (filters?.year) {
        const startDate = `${filters.year}-01-01`;
        const endDate = `${filters.year}-12-31`;
        query = query.gte('meet_date', startDate).lte('meet_date', endDate);
      }

      if (filters?.quarter) {
        const quarterStartMonth = (filters.quarter - 1) * 3 + 1;
        const quarterEndMonth = quarterStartMonth + 2;
        const year = filters.year || new Date().getFullYear();
        const startDate = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(quarterEndMonth).padStart(2, '0')}-${quarterEndMonth === 12 ? '31' : '30'}`;
        query = query.gte('meet_date', startDate).lte('meet_date', endDate);
      }

      if (filters?.month) {
        const year = filters.year || new Date().getFullYear();
        const startDate = `${year}-${String(filters.month).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(filters.month).padStart(2, '0')}-${filters.month === 2 ? '28' : filters.month === 12 ? '31' : '30'}`;
        query = query.gte('meet_date', startDate).lte('meet_date', endDate);
      }

      const { data, error } = await query.order('meet_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        console.log('No meetings data returned');
        return [];
      }

      console.log('Meetings data:', data);

      return data.map(meeting => ({
        meet_comm: meeting.meet_comm,
        meet_date: meeting.meet_date,
        meet_minutes: meeting.meet_minutes,
        comm_name: meeting.meet_comm // Using meet_comm as the community name
      }));
    } catch (err) {
      console.error('Error fetching meetings:', err);
      throw err;
    }
  },

  getStats: async (filters?: MeetingFilters): Promise<MeetingStats> => {
    try {
      const meetings = await supabaseMeetingService.getAll(filters);

      // Get unique communities with meetings
      const communitiesWithMeetings = [...new Set(meetings.map(m => m.meet_comm))];

      // Calculate meetings per month
      const meetingsPerMonth = meetings.reduce((acc, meeting) => {
        const date = new Date(meeting.meet_date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        const existingEntry = acc.find(e => e.year === year && e.month === month);
        if (existingEntry) {
          existingEntry.count++;
        } else {
          acc.push({ year, month, count: 1 });
        }

        return acc;
      }, [] as { year: number; month: number; count: number }[]);

      // Sort by year and month
      meetingsPerMonth.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      return {
        totalMeetings: meetings.length,
        meetingsPerMonth,
        communitiesWithMeetings
      };
    } catch (err) {
      console.error('Error getting meeting stats:', err);
      throw err;
    }
  }
};
